import React from 'react';
import { useForm } from '@hum/common/src/modules/form';
import { Button, ButtonType } from '@hum/ui-library';
import { Alert } from '@hum/legacy-ui';
import {
  FormFields,
  AddPortfolioInvestorForm,
  validationSchema,
  initialValues as defaultValues,
} from './FormFields';
import * as ds from '@hum/design-system';
import { createCompany, useApiMutation } from '@hum/api';
import { toast } from '@hum/common/src/modules/toast';

interface AddCompanyProps {
  onClose: () => void;
  title: string;
  visible?: boolean;
}

export const AddPortfolioInvestor: React.FC<AddCompanyProps> = ({
  onClose,
  visible,
}) => {
  const initialValues: AddPortfolioInvestorForm = defaultValues;
  const { mutate: createFund, isLoading: loading } = useApiMutation(
    createCompany,
    {
      onSuccess: () => {
        toast.success('Fund created successfully!');
        closeForm();
      },
      onError: () => {
        toast.error('Failed to create fund');
      },
    }
  );

  const form = useForm({
    initialValues,
    validationSchema,
    onSubmit: (rawVariables: AddPortfolioInvestorForm) => {
      const fullWebsite =
        rawVariables.website.startsWith('http://') ||
        rawVariables.website.startsWith('https://')
          ? rawVariables.website.toLowerCase()
          : `https://${rawVariables.website.toLowerCase()}`;
      return createFund({
        name: rawVariables.name,
        website: fullWebsite,
        companyRole: 'syndication_investor',
      });
    },
  });

  const error = form.status;

  const closeForm = () => {
    onClose();
    form.resetForm();
  };

  return (
    <ds.Modal
      scrollable
      fullHeight
      onClose={onClose}
      header="Add fund"
      visible={visible}
      veryWide
      footer={
        <>
          <div>
            <Button
              type={ButtonType.SECONDARY}
              testId="manage_company:cancel_edit"
              onClick={() => closeForm()}
              disabled={loading || form.isSubmitting}
            >
              Cancel
            </Button>
          </div>

          <Button
            testId="save-button"
            onClick={form.handleSubmit}
            disabled={loading || form.isSubmitting}
            loading={form.isSubmitting}
          >
            Add
          </Button>
        </>
      }
    >
      <form data-test-id="edit-investor-form" onSubmit={form.handleSubmit}>
        <FormFields includePosition form={form} showDescriptionFields />
      </form>

      {error ? <Alert negative title={error} /> : null}
    </ds.Modal>
  );
};
